import { useState, useEffect } from 'react';
import { quotationCatalogAPI, quotationsAPI } from '../../services/quotationService';
import QuotationForm from './QuotationForm';
import QuotationPreview from './QuotationPreview';

const INITIAL_DATA = {
  status: 'borrador',
  client_name: '',
  client_greeting: 'Estimado/a',
  prospect_phone: null,
  agent_identity_id: null,
  campaign_id: null,
  reference_type: '',
  items: [],
  alternatives: [],
  total_neto: 0,
  iva_amount: 0,
  total_con_iva: 0,
  validity_start: '',
  validity_end: '',
  notes: '',
};

export default function QuotationEditor({ quotationId, onBack }) {
  const [formData, setFormData] = useState({ ...INITIAL_DATA });
  const [catalog, setCatalog] = useState({ products: [], kits: [], campaigns: [], agents: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(quotationId || null);

  // Load catalog on mount
  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const [products, kits, campaigns, agents] = await Promise.all([
          quotationCatalogAPI.getProducts(),
          quotationCatalogAPI.getKits(),
          quotationCatalogAPI.getCampaigns(),
          quotationCatalogAPI.getAgents(),
        ]);
        setCatalog({ products, kits, campaigns, agents });
      } catch (err) {
        console.error('Error loading catalog:', err);
      }
    };
    loadCatalog();
  }, []);

  // Load existing quotation if editing
  useEffect(() => {
    if (quotationId) {
      quotationsAPI.get(quotationId).then(q => {
        setFormData({
          status: q.status || 'borrador',
          client_name: q.client_name || '',
          client_greeting: q.client_greeting || 'Estimado/a',
          prospect_phone: q.prospect_phone,
          agent_identity_id: q.agent_identity_id,
          campaign_id: q.campaign_id,
          reference_type: q.reference_type || '',
          items: q.items || [],
          alternatives: q.alternatives || [],
          total_neto: q.total_neto || 0,
          iva_amount: q.iva_amount || 0,
          total_con_iva: q.total_con_iva || 0,
          validity_start: q.validity_start ? q.validity_start.slice(0, 10) : '',
          validity_end: q.validity_end ? q.validity_end.slice(0, 10) : '',
          notes: q.notes || '',
        });
        setSavedId(q.id);
        setLoading(false);
      }).catch(err => {
        console.error('Error loading quotation:', err);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [quotationId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...formData };
      if (savedId) {
        await quotationsAPI.update(savedId, payload);
      } else {
        const created = await quotationsAPI.create(payload);
        setSavedId(created.id);
      }
    } catch (err) {
      console.error('Error saving:', err);
      alert('Error al guardar: ' + err.message);
    }
    setSaving(false);
  };

  const handleDownload = async () => {
    // Save as 'emitida' after PDF download
    try {
      const payload = { ...formData, status: 'emitida' };
      if (savedId) {
        await quotationsAPI.update(savedId, payload);
      } else {
        const created = await quotationsAPI.create(payload);
        setSavedId(created.id);
      }
      setFormData(prev => ({ ...prev, status: 'emitida' }));
    } catch (err) {
      console.error('Error saving after download:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="flex items-center gap-4 px-4 py-3 border-b border-gray-200 bg-white">
        <button onClick={onBack} className="text-blue-600 hover:underline text-sm">
          &larr; Volver a lista
        </button>
        <h1 className="text-lg font-bold text-gray-800">
          {quotationId ? 'Editar Cotizacion' : 'Nueva Cotizacion'}
        </h1>
        {savedId && (
          <span className="text-xs text-gray-400">ID: {savedId}</span>
        )}
        {saving && <span className="text-xs text-blue-500">Guardando...</span>}
      </div>

      {/* Split panel */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel - Form */}
        <div className="w-2/5 overflow-y-auto border-r border-gray-200 p-4 bg-white">
          <QuotationForm data={formData} catalog={catalog} onChange={setFormData} />
        </div>

        {/* Right panel - Preview */}
        <div className="w-3/5 overflow-y-auto bg-gray-100 p-4">
          <QuotationPreview
            data={formData}
            catalog={catalog}
            onSave={handleSave}
            onDownload={handleDownload}
          />
        </div>
      </div>
    </div>
  );
}
